import React, { useEffect } from "react";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { AllTotalsHomepageThunk } from "../../redux/states/cartCollection/thunk";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const dispatch = useDispatch();

  const { homepagetotals, loading } = useSelector((state) => state.cart);

  useEffect(() => {
    dispatch(AllTotalsHomepageThunk());
  }, [dispatch]);

  // API Values (fallback if null)
  const totalOrders = homepagetotals?.total_orders || 0;
  const totalEarnings = homepagetotals?.total_earnings || 0;

  const todayOrders = homepagetotals?.today_orders || 0;
  const todayEarnings = homepagetotals?.today_earnings || 0;

  const monthOrders = homepagetotals?.this_month_orders || 0;
  const monthEarnings = homepagetotals?.this_month_earnings || 0;

  const statsData = [
    {
      title: "Today's Orders",
      value: todayOrders,
      subtitle: "New Orders Today",
      icon: "🛒",
      color: "bg-blue-500",
    },
    {
      title: "Today's Earnings",
      value: `₹${Number(todayEarnings).toLocaleString()}`,
      subtitle: "Revenue Earned Today",
      icon: "💰",
      color: "bg-green-500",
    },
    {
      title: "This Month Orders",
      value: monthOrders,
      subtitle: "Orders In Current Month",
      icon: "📦",
      color: "bg-indigo-500",
    },
    {
      title: "This Month Earnings",
      value: `₹${Number(monthEarnings).toLocaleString()}`,
      subtitle: "Revenue In Current Month",
      icon: "🏦",
      color: "bg-yellow-500",
    },
    {
      title: "Total Orders",
      value: totalOrders,
      subtitle: "All Time Orders",
      icon: "📊",
      color: "bg-purple-500",
    },
    {
      title: "Total Earnings",
      value: `₹${Number(totalEarnings).toLocaleString()}`,
      subtitle: "All Time Revenue",
      icon: "💎",
      color: "bg-orange-500",
    },
  ];

  // Chart Example (still static for now)
  const orderPerformanceData = {
    labels: ["Today", "This Month"],
    datasets: [
      {
        label: "Orders",
        data: [todayOrders, monthOrders],
        backgroundColor: "#3B82F6",
        borderRadius: 6,
      },
    ],
  };

  const orderPerformanceOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Orders Summary" },
    },
  };

  return (
    <NonAuthLayout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>

        <div className="text-sm text-gray-500">
          {loading ? "Loading data..." : "Welcome back, Admin 👋"}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {statsData.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.subtitle}</p>
              </div>

              <div
                className={`${stat.color} w-10 h-10 rounded-lg flex items-center justify-center text-white text-xl`}
              >
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Orders Chart */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Orders Summary
        </h3>

        <div className="h-80">
          <Bar data={orderPerformanceData} options={orderPerformanceOptions} />
        </div>
      </div>
    </NonAuthLayout>
  );
}
